import { Box, Checkbox, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import {
  apiRequest,
  catchErrorMessageHandler,
} from "../../../infrastructure/backendService";
import apiPath from "../../../config/apiPaths.json";
import ErrorPopup from "../../common/ErrorPopup";
import styles from "../ViewRequests/ViewRequests.module.scss";
import printIcon from "../../../assets/PrintIcon.svg";
import ViewExpenseRequest from "../PrintVoucher/ViewExpenseRequest/ViewExpenseRequest";
import Pagination from "../../common/pagination/pagination";
import { CommonButton } from "../../common/StyledComponents/StyledFields";
import APILoading from "../../common/APILoading/APILoading";
import ButtonLoader from "../../common/ButtonLoader/ButtonLoader";
import SuccessPopup from "../../devotee/donation/successPopup";
import editIcon from "../../../assets/EditIcon.svg";
import PageLoader from "../../common/PageLoader/PageLoader";

const ApprovedExpenses = () => {
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [approvedExpenses, setApprovedExpenses] = useState<any>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [openExpenseRequestDialog, setOpenExpenseRequestDialog] =
    useState(false);
  const [viewRequestDetails, setViewRequestDetails] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState<string>("");
  const [selectAllFiles, setSelectAllFiles] = useState(false);
  const [referenceNumbersList, setReferenceNumbersList] = useState<string[]>(
    []
  );
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const getApprovedExpenses = () => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.findAllApprovedExpenseDetails,
      includeToken: true,
      params: {
        page: pageNumber - 1,
        size: 50,
        direction: "desc",
        sortBy: "dateEntered",
      },
    })
      .then((res) => {
        setIsPageLoading(false);
        setPageLoading(false);
        setIsBtnLoading(false);
        setApprovedExpenses(res?.data?.content);
        setTotalRecords(res?.data?.totalElements);
        setReferenceNumbersList([]);
        setSelectAllFiles(false);
        const element = document.getElementById("expense_details");
        if (element) {
          element?.scrollIntoView({ behavior: "smooth", block: "start" });
          // Apply additional offset for a custom position
          window.scrollTo({
            top: 300,
            left: 0,
            behavior: "smooth",
          });
        }
      })
      .catch((err) => {
        setIsBtnLoading(false);
        setIsPageLoading(false);
        setPageLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  useEffect(() => {
    setPageLoading(true);
    getApprovedExpenses();
  }, [pageNumber]);

  const handlePrint = (referenceNum: any) => {
    setOpenExpenseRequestDialog(true);
    setReferenceNumber(referenceNum);
  };

  const handleView = (referenceNum: any) => {
    setOpenExpenseRequestDialog(true);
    setViewRequestDetails(true);
    setReferenceNumber(referenceNum);
  };

  const handleUpdate = (referenceNum: any) => {
    setIsUpdate(true);
    setOpenExpenseRequestDialog(true);
    setReferenceNumber(referenceNum);
  };

  const handleCheckBoxChange = (value: boolean, referenceNumber: string) => {
    setSelectAllFiles(false);
    if (referenceNumbersList?.includes(referenceNumber)) {
      let refNums = referenceNumbersList.filter(
        (item: string) => item !== referenceNumber
      );
      setReferenceNumbersList([...refNums]);
    } else {
      setReferenceNumbersList([...referenceNumbersList, referenceNumber]);
    }
  };

  const handleSelectAllFiles = () => {
    const isChecked = selectAllFiles;
    setSelectAllFiles(!isChecked);
    let refNums: any = [];
    if (!isChecked) {
      approvedExpenses?.forEach((each: any) => {
        if (each?.canFile) {
          refNums?.push(each?.referenceNumber);
        }
      });
    }
    setReferenceNumbersList(refNums);
  };

  const handleFileApprovedExpenses = () => {
    setIsBtnLoading(true);
    apiRequest({
      method: "PUT",
      endpoint: apiPath.fileApprovedExpense,
      includeToken: true,
      body: referenceNumbersList,
    })
      .then(() => {
        getApprovedExpenses();
        toast.custom(
          <SuccessPopup
            headerMessage="Success"
            showMessage={`Successfully filed the expenses`}
          />
        );
      })
      .catch((err) => {
        setIsBtnLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  const renderHeaderSection = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: { xs: "flex-start", sm: "space-between" },
        }}
      >
        <Typography className="sub_heading">Approved Expenses</Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", mr: "15px" }}>
            <Checkbox
              checked={selectAllFiles}
              onChange={handleSelectAllFiles}
              name={"allFiles"}
              sx={{
                p: "0px",
                color: "#F05802 !important",
              }}
            />
            <Typography>Select All</Typography>
          </Box>
          <CommonButton
            onClick={handleFileApprovedExpenses}
            disabled={!(referenceNumbersList?.length > 0)}
          >
            <ButtonLoader isBtnLoading={isBtnLoading} btnText={"File"} />
          </CommonButton>
        </Box>
      </Box>
    );
  };

  const renderApprovedExpensesTable = () => {
    return (
      <Box mt="20px" className={styles.expense_request_details_container}>
        {approvedExpenses?.length > 0 ? (
          <>
            <Box
              sx={{ mt: "15px", width: "100%", overflowX: "auto" }}
              id="expense_details"
            >
              <table className={styles.table_container}>
                <thead>
                  <th>Sr No</th>
                  <th>Submitted on</th>
                  <th>
                    Short Title <br />
                    (Reference Number)
                  </th>
                  <th>Amount</th>
                  <th className="display_none_ipad">Expense Details</th>
                  <th>Check Mailed To</th>
                  <th>Check Number</th>
                  <th>Check Mailed Date</th>
                  <th>File</th>
                  <th>Action</th>
                </thead>
                <tbody>
                  {approvedExpenses?.map?.((row: any, index: number) => (
                    <tr key={index}>
                      <td>{index + 1 + (pageNumber - 1)*50}</td>
                      <td>{row?.submittedOn}</td>
                      <td
                        className={styles.table_desc_txt}
                        onClick={() => handleView(row?.referenceNumber)}
                        style={{
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        {row?.shortTitle} ({row?.referenceNumber})
                      </td>
                      <td className={styles.amount_text}>
                        ${row?.amount?.toFixed(2)}
                      </td>
                      <td
                        className={`${styles.table_desc_txt} display_none_ipad`}
                      >
                        {row?.expenseDetails}
                      </td>
                      <td className={`${styles.table_desc_txt}`}>
                        {row?.checkMailTo ?? "-"}
                      </td>
                      <td>{row?.checkNumber ?? "-"}</td>
                      <td>{row?.checkMailedDate ?? "-"}</td>
                      <td>
                        {row?.canFile ? (
                          <Checkbox
                            checked={referenceNumbersList?.includes(
                              row?.referenceNumber
                            )}
                            onChange={() => {
                              handleCheckBoxChange(
                                row?.isChecked,
                                row?.referenceNumber
                              );
                            }}
                            name={"file"}
                            sx={{
                              mr: "5px",
                              p: "0px",
                              color: "#F05802 !important",
                            }}
                          />
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        <Box sx={{ display: "flex", textAlign: "center" }}>
                          <Box
                            component={"img"}
                            src={printIcon}
                            alt="Print"
                            onClick={() => handlePrint(row?.referenceNumber)}
                            sx={{ cursor: "pointer", mr: "15px" }}
                          />
                          <Box
                            component={"img"}
                            src={editIcon}
                            alt="edit"
                            onClick={() => handleUpdate(row?.referenceNumber)}
                            sx={{ cursor: "pointer" }}
                          />
                        </Box>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
            {totalRecords > 0 && (
              <Box
                sx={{ display: "flex", justifyContent: "flex-end", mt: "15px" }}
              >
                <Pagination
                  items={totalRecords}
                  itemsPerPage={20}
                  setPageNumber={setPageNumber}
                  pageNumber={pageNumber}
                />
              </Box>
            )}
          </>
        ) : (
          <Typography
            sx={{ mt: "15px" }}
            className={styles.expense_request_txt}
          >
            There are no approved requests.
          </Typography>
        )}
      </Box>
    );
  };

  const render = () => {
    return (
      <>
        {renderHeaderSection()}
        {renderApprovedExpensesTable()}
        {openExpenseRequestDialog && (
          <ViewExpenseRequest
            open={openExpenseRequestDialog}
            handleClose={() => {
              setOpenExpenseRequestDialog(false);
              setReferenceNumber("");
              setViewRequestDetails(false);
              if (isUpdate) {
                getApprovedExpenses();
                setIsUpdate(false);
              }
            }}
            referenceNumber={referenceNumber}
            isApprovedExpense={isUpdate}
            viewRequest={viewRequestDetails}
          />
        )}
      </>
    );
  };

  return (
    <>
      <Toaster />
      {isPageLoading ? <APILoading /> : render()}
      <PageLoader loading={!isPageLoading ? pageLoading: false} />
    </>
  );
};

export default ApprovedExpenses;
